import { Column } from '@orion-platform/datagrid';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { VendorName } from '../../models/commit.model';
import {
  CommitsFilterDto,
  CommitsFilterUIModel,
  DynamicDateKey,
} from '../../models/supply-visibility.model';
import { PartNumberVendorCodes, VendorCode } from '../../models/vendor-codes-rights.model';
import { getDateWithTime } from '../../utils/date.utils';
import { splitWith } from '../../utils/string.utils';
import { BaseCommitDto, WithDynamicDateKeys } from './supply-visibility.types';

/**
 * Orders and filters default field definitions according to `formFields`,
 * adds `index`, and (if present) forces the `"action"` field to be first.
 * @param {string[]} formFields - Desired order of fields (e.g., from a form).
 * @param {T[]} defaults - Default field definitions.
 * @param {keyof T} [key='field'] - Property in `defaults` used to match `formFields`.
 * @returns {(T & { index: number })[]} Ordered items with `index`.
 *
 * Notes:
 * - Case-insensitive matching.
 * - Duplicate `formFields` are ignored (first occurrence wins).
 * - If `defaults` includes an `"action"` field, it is placed at index 0.
 */
export const orderDefaults = <T extends Column>(
  formFields: string[],
  defaults: T[],
  key: keyof T = 'field' as keyof T,
): Array<T & { index: number }> => {
  // Lookup: default item by normalized key (lowercased).
  const byKey = new Map<string, T>();
  for (let i = 0; i < defaults.length; i++) {
    const d = defaults[i];
    byKey.set(String(d[key]).toLowerCase(), d);
  }

  // Prepare result and helpers.
  const seen = new Set<string>();
  const result: Array<T & { index: number }> = [];

  // 1) If there's an "action" field in defaults, put it first.
  const ACTION = 'actions';
  const actionItem = byKey.get(ACTION);
  if (actionItem) {
    result.push({ ...actionItem, index: 0 });
    seen.add(ACTION);
  }

  // 2) Walk formFields in order; append matching defaults (skip duplicates and "action").
  for (let i = 0; i < formFields.length; i++) {
    const k = formFields[i].toLowerCase();
    if (seen.has(k)) continue; // skip if already added (including "action").
    const d = byKey.get(k);
    if (d) {
      result.push({ ...d, index: result.length }); // index reflects final position.
      seen.add(k);
    }
  }

  return result;
};

export const vendorsToUpper = (vendorCodes: string[]): string[] => {
  if (!vendorCodes?.length) return [];
  return vendorCodes.map((code): string => (code ? Helper.virtualVCReplace(code) : ''));
};

export const applyDateFilter = (
  filter: Pick<CommitsFilterUIModel, 'dateFrom' | 'dateTo' | 'dateType'>,
  dto: WithDynamicDateKeys & { plannedOrderStatus?: string[] },
): void => {
  if (filter.dateType === 'plannedOrderStatus') {
    dto.plannedOrderStatus = ['OPEN'];
    return;
  }

  if (filter.dateFrom != null) {
    dto[`${filter.dateType}From` as DynamicDateKey] = getDateWithTime(
      filter.dateFrom,
      'T00:00:00.000Z',
    );
  }

  if (filter.dateTo != null) {
    dto[`${filter.dateType}To` as DynamicDateKey] = getDateWithTime(
      filter.dateTo,
      'T00:00:00.000Z',
    );
  }
};

export const buildBaseCommitDto = (filter: CommitsFilterDto, email: string): BaseCommitDto => {
  // Normalise `status` to the `string[]` shape the backend expects. Legacy
  // cached filters (persisted before the array-normalisation was in place)
  // could hold `status` as a scalar string like `"D"`; passing that
  // straight through triggers the 500 `Could not cast or convert from
  // System.String to List<String>` — see the sibling coercion in
  // `commits-filter-form.emitSearch` for the client-side guard.
  const rawStatus: unknown = filter.status;
  const status: string[] = Array.isArray(rawStatus)
    ? (rawStatus as string[])
    : typeof rawStatus === 'string' && rawStatus.length > 0
      ? rawStatus.split(',').filter((s: string): boolean => s.length > 0)
      : [];
  return {
    buyers: splitWith(filter.buyers),
    contacts: filter.contacts ? splitWith(email) : [],
    ContainerNumber: splitWith(filter.ContainerNumber),
    Forwarder: filter.Forwarder,
    inboundDeliveryNumbers: splitWith(filter.inboundDeliveryNumbers),
    invoiceNumbers: splitWith(filter.invoiceNumbers),
    partNumbers: splitWith(filter.partNumbers),
    purchaseOrderLines: splitWith(filter.purchaseOrderLines),
    purchaseOrderNumbers: splitWith(filter.purchaseOrderNumbers),
    Remark: splitWith(filter.Remark),
    status,
    trackNumbers: splitWith(filter.trackNumbers),
    vendorCodes: splitWith(filter.vendorCodes),
  };
};

export function normalizeVendorsList(
  vendors: VendorName[] | { result: VendorName[] },
): VendorName[] {
  return Array.isArray(vendors) ? vendors : (vendors && vendors.result) || [];
}

export function buildVendorNameMap(vendorsList: VendorName[]): Record<string, string> {
  return (vendorsList || []).reduce<Record<string, string>>((acc, v): Record<string, string> => {
    const code = v.vendorCode || '';
    const name = v.vendorName || '';
    if (code) acc[code.toUpperCase()] = name;
    return acc;
  }, {});
}

export function transformItemsWithVendorNames(
  items: PartNumberVendorCodes[],
  nameMap: Record<string, string>,
): VendorCode[] {
  return items.map((t): VendorCode => {
    const vendorCodes = ((t.vendorCodes as string[]) || []).map((vc): string => {
      if (!vc) return '';
      if (typeof vc === 'string') {
        const code = vc.toUpperCase();
        const name = nameMap[code];
        return name ? `${vc} | ${name}` : vc;
      }
      return vc;
    });
    return { partNumber: t.partNumber, vendorCodes: vendorCodes };
  });
}

/**
 * Transform vendor codes response to formatted strings
 */
export function transformVendorCodesResponse(response: PartNumberVendorCodes[]): VendorCode[] {
  return (response || []).map((item): VendorCode => {
    const partNumber = item.partNumber || '';
    const rawVendorCodes = item.vendorCodes || [];
    const vendorCodes = (rawVendorCodes || []).map((v): string => {
      if (!v) return '';
      if (typeof v === 'string') return v;
      const code = v.vendorCode || '';
      const name = v.vendorName || '';
      if (code && name) return `${code} | ${name}`;
      if (typeof v === 'object') return Object.values(v).filter(Boolean).join(' | ');
      return String(v);
    });
    return { partNumber: partNumber, vendorCodes: vendorCodes };
  });
}
